import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Swal from "sweetalert2";
import { createAgreement } from "../../services/agreementsService";
import ImageUploaderWithCrop from "../images/ImageUploaderWithCrop";
import ModalTitle from "../common/ModalTitle";

// Esquema de validación con Yup
const AgreementSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'El título debe tener al menos 3 caracteres')
    .max(100, 'El título no puede exceder los 100 caracteres')
    .required('El título es obligatorio'),
  description: Yup.string()
    .min(10, 'La descripción debe tener al menos 10 caracteres')
    .max(500, 'La descripción no puede exceder los 500 caracteres')
    .required('La descripción es obligatoria'),
  contact: Yup.string().email('El contacto debe ser un correo electrónico válido')
    .min(5, 'El contacto debe tener al menos 5 caracteres')
    .required('El contacto es obligatorio'),
  company: Yup.string()
    .required('Debe seleccionar una empresa'),
  image: Yup.mixed()
    .required('La imagen es obligatoria')
    .test(
      'fileFormat',
      'Solo se permiten imágenes en formato JPG o PNG',
      value => value && ['image/jpeg', 'image/png'].includes(value.type)
    )
    .test(
      'fileSize',
      'La imagen no puede exceder 2MB',
      value => value && value.size <= 2 * 1024 * 1024
    )
});

const AgreementForm = ({ open, onClose, companies, user, onSuccess, onActionLoader }) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [imageName, setImageName] = useState("");
  const [resetCounter, setResetCounter] = useState(0);

  if (!open) return null;

  const handleSubmit = async (values, { resetForm }) => {
    try {
      handleCloseModal();
      const confirmation = await Swal.fire({
        title: '¿Estás seguro?',
        text: "¿Quieres crear este convenio?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#C42251',
        confirmButtonText: 'Sí, crear convenio',
        cancelButtonText: 'Cancelar'
      });

      if (!confirmation.isConfirmed) {
        return;
      }
      
      onActionLoader(true);
      const response = await createAgreement(values, user);
      onActionLoader(false);
      
      if (response.success) {
        Swal.fire({
          title: '¡Éxito!',
          text: 'El convenio ha sido creado exitosamente.',
          icon: 'success',
          confirmButtonColor: '#0488AF',
          confirmButtonText: 'Aceptar'
        });
        resetForm();
        setImagePreview(null);
        setImageName("");
        onSuccess();
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Hubo un error al crear el convenio.',
          icon: 'error',
          confirmButtonColor: '#C42251',
          confirmButtonText: 'Aceptar'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Hubo un error al crear el convenio.',
        icon: 'error',
        confirmButtonColor: '#C42251',
        confirmButtonText: 'Aceptar'
      });
      onActionLoader(false);
      console.error("Error al subir convenio:", error);
    }
  };

  const handleCloseModal = () => {
    setResetCounter(prev => prev + 1);
    setImagePreview(null);
    onClose();
  }

  const inputClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none shadow-sm";
  const selectClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none appearance-none bg-white shadow-sm";
  const labelClass = "block mb-2 font-medium text-gray-700";
  const helperTextClass = "text-xs text-gray-500 mt-1";
  const sectionClass = "p-6 bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-xl";
  const errorClass = "text-red-500 text-sm mt-1";
  const buttonClass = "w-full py-3 px-4 rounded-lg transition-all duration-300 font-medium";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto">
        <ModalTitle title="Crear Convenio" />
        <div className={sectionClass}>
          <Formik
            initialValues={{ 
              title: '', 
              description: '', 
              contact: '', 
              image: null, 
              company: '' 
            }}
            validationSchema={AgreementSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, isValid, dirty, isSubmitting, setFieldTouched, setFieldError }) => (
              <Form className="space-y-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Sección Imagen */}
                  <div className="w-full md:w-2/5">
                    <label className={labelClass}>Imagen del Convenio</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                      <ImageUploaderWithCrop
                        aspectRatio={16 / 9}
                        onCropped={(blob) => {
                          if (imagePreview) URL.revokeObjectURL(imagePreview);
                          const url = URL.createObjectURL(blob);
                          setFieldValue("image", blob);
                          setImagePreview(url);
                          setFieldTouched("image", true, false);
                        }}
                        setFieldTouched={setFieldTouched}
                        setFieldError={setFieldError}
                        setFieldValue={setFieldValue}
                        maxFileSize={2 * 1024 * 1024} // 2MB
                        fieldName="image"
                        values={values}
                        resetKey={resetCounter}
                      />
                      <p className={helperTextClass}>Formatos: JPG, PNG. Tamaño máximo: 2MB</p>
                      {touched.image && errors.image && (
                        <p className={errorClass}>{errors.image}</p>
                      )}

                      {imagePreview && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Vista previa:</h4>
                          <img
                            src={imagePreview}
                            alt="Vista previa"
                            className="rounded-lg shadow-md w-full object-cover h-48"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sección Formulario */}
                  <div className="w-full md:w-3/5 space-y-4">
                    <div>
                      <label htmlFor="title" className={labelClass}>Título</label>
                      <Field 
                        name="title" 
                        id="title" 
                        type="text" 
                        className={inputClass} 
                        placeholder="Ingrese el título del convenio"
                      />
                      <ErrorMessage name="title" component="p" className={errorClass} />
                    </div>

                    <div>
                      <label htmlFor="description" className={labelClass}>Descripción</label>
                      <Field 
                        as="textarea" 
                        name="description" 
                        id="description" 
                        rows="4" 
                        className={inputClass} 
                        placeholder="Ingrese la descripción del convenio"
                      />
                      <ErrorMessage name="description" component="p" className={errorClass} />
                    </div>

                    <div>
                      <label htmlFor="contact" className={labelClass}>Contacto (Email)</label>
                      <Field 
                        name="contact" 
                        id="contact" 
                        type="email" 
                        className={inputClass} 
                        placeholder="correo@ejemplo.com"
                      />
                      <ErrorMessage name="contact" component="p" className={errorClass} />
                    </div>

                    <div>
                      <label htmlFor="company" className={labelClass}>Empresa</label>
                      <Field 
                        as="select" 
                        name="company" 
                        id="company" 
                        className={selectClass}
                      >
                        <option value="">Seleccione empresa</option>
                        {companies.map((company, i) => (
                          <option key={i} value={company}>
                            {company}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="company" component="p" className={errorClass} />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-6 border-t border-gray-200 mt-8">
                  <button 
                    type="button" 
                    onClick={handleCloseModal}
                    className={`${buttonClass} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit"
                    disabled={isSubmitting || !(isValid && dirty)}
                    className={`${buttonClass} bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white hover:from-[#071f3d] hover:to-[#03779C] disabled:opacity-70`}
                  >
                    Guardar Convenio
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default AgreementForm;